import { Settings, ListChecks, DollarSign, Award, CalendarClock } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { recurringSetupEntry } from '@/utils/recurringSetupEntry'
import {
  formatNegotiationDeadlineSlideLabel,
  toNegotiationDateInputValue,
} from '@/utils/negotiationDeadline'
import { DatetimeLocalField } from '@/components/Deals/DealDetailPanel/DatetimeLocalField'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Input'
import { SUPPORT_PRICING_OPTIONS } from '@/components/Configuracao/plansTabUtils'
import type { useProposalStudio } from './useProposalStudio'

type Studio = ReturnType<typeof useProposalStudio>

export function ProposalStudioPlanForm({ s }: { s: Studio }) {
  if (!s.activePlan) return null
  return (
    <div className="flex flex-col gap-4">
                      {/* Form section headers */}
                      <div className="flex justify-between items-center pb-2 border-b border-white/[0.06]">
                        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">
                          Editar Escopo & Valores
                        </h3>
                      </div>

                      {/* Navigation Tabs */}
                      <div className="flex border-b border-white/[0.06] p-0.5 bg-white/[0.02] rounded-lg">
                        <button
                          type="button"
                          onClick={() => s.setActiveFormTab('details')}
                          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            s.activeFormTab === 'details' 
                              ? 'bg-accent text-white shadow-lg' 
                              : 'text-text-muted hover:text-text-primary'
                          }`}
                        >
                          <Settings size={13} />
                          Valores & Regras
                        </button>
                        <button
                          type="button"
                          onClick={() => s.setActiveFormTab('scope')}
                          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            s.activeFormTab === 'scope' 
                              ? 'bg-accent text-white shadow-lg' 
                              : 'text-text-muted hover:text-text-primary'
                          }`}
                        >
                          <ListChecks size={13} />
                          Entregáveis ({s.activePlan.services?.reduce((acc: number, cat: any) => acc + (cat.services?.filter((s: any) => s.isIncluded).length || 0), 0) || 0} Ativos)
                        </button>
                      </div>

                      {/* Details tab */}
                      {s.activeFormTab === 'details' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                              <Input
                                label="Nome do Plano"
                                value={s.activePlan.descricao || ''}
                                onChange={(e) => s.handleUpdateActivePlanField('descricao', e.target.value)}
                                placeholder="Ex: Plano START, Desenvolvimento..."
                              />
                            </div>
                            <div>
                              <Input
                                label="Duração (meses)"
                                type="number"
                                min={1}
                                value={s.activePlan.duration || 12}
                                onChange={(e) => s.handleUpdateActivePlanField('duration', Math.max(1, Number(e.target.value)))}
                              />
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-1.5">
                            <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--font-color-label)]">
                              Tipo comercial
                            </span>
                            <div className="rounded-md border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-text-primary">
                              Plano mensal recorrente
                            </div>
                          </div>

                          <Textarea
                            label="Descrição do Plano"
                            rows={3}
                            value={s.activePlan.description || ''}
                            onChange={(e) => s.handleUpdateActivePlanField('description', e.target.value)}
                            placeholder="Texto que aparece no slide e no PDF"
                          />
                          <p className="text-[10px] text-text-muted -mt-2">
                            Vale para esta proposta. Salve para o slide e a exportação usarem este texto.
                          </p>

                          {/* Pricing Structure */}
                          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
                            <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
                              <h4 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-1.5">
                                <DollarSign size={14} />
                                Modelo Comercial
                              </h4>
                              <span className="rounded-md bg-accent px-2.5 py-1 text-[10px] font-semibold text-white">
                                Plano mensal
                              </span>
                            </div>

                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {SUPPORT_PRICING_OPTIONS.map((option) => {
                                  const active =
                                    (s.activePlan?.pricing?.supportPricingMode ?? 'FIXO_2000') === option.id
                                  return (
                                    <button
                                      key={option.id}
                                      type="button"
                                      onClick={() =>
                                        s.handleUpdateActivePlanPricing({
                                          supportPricingMode: option.id,
                                          installmentValue: option.baseValue,
                                          recorrente: true,
                                          monthlyFixedValue: 2000,
                                          monthlyBaseValue: 500,
                                          revenueSharePercent:
                                            'revenueSharePercent' in option
                                              ? option.revenueSharePercent
                                              : 0,
                                        })
                                      }
                                      className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                                        active
                                          ? 'border-accent bg-accent/10 text-text-primary'
                                          : 'border-white/[0.06] text-text-muted hover:text-text-primary'
                                      }`}
                                    >
                                      <span className="block font-semibold">{option.label}</span>
                                      <span className="block text-[11px]">{option.description}</span>
                                    </button>
                                  )
                                })}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <Input
                                  label={
                                    s.activePlan.pricing?.supportPricingMode === 'BASE_500_MAIS_1_FATURAMENTO'
                                      ? 'Base mensal (BRL)'
                                      : 'Valor Mensal (BRL)'
                                  }
                                  type="number"
                                  min={0}
                                  value={s.activePlan.pricing?.installmentValue || ''}
                                  onChange={(e) => s.handleUpdateActivePlanPricing({ installmentValue: e.target.value === '' ? '' : Number(e.target.value) })}
                                />
                                <Input
                                  label="Implantação (Opcional)"
                                  type="number"
                                  min={0}
                                  value={recurringSetupEntry(s.activePlan.pricing) || ''}
                                  onChange={(e) => s.handleUpdateActivePlanPricing({ entry: e.target.value === '' ? '' : Number(e.target.value) })}
                                  placeholder="Ex: 2500"
                                />
                              </div>
                            </>

                            <div className="text-right text-[10px] text-text-muted mt-1">
                              Cálculo recorrente: {formatCurrency(s.activePlan.pricing?.installmentValue || 0)} / mês
                            </div>
                          </div>

                          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
                            <div className="flex items-center gap-1.5 border-b border-white/[0.06] pb-2">
                              <CalendarClock size={14} className="text-accent" />
                              <h4 className="text-xs font-bold text-accent uppercase tracking-wider">
                                Validade da negociação
                              </h4>
                            </div>
                            <DatetimeLocalField
                              dateOnly
                              attention
                              label="Fechamento até"
                              placeholder="Toque para escolher a data"
                              value={toNegotiationDateInputValue(s.activePlan.negotiationValidUntil)}
                              onChange={(next) =>
                                s.handleUpdateActivePlanField(
                                  'negotiationValidUntil',
                                  next || undefined,
                                )
                              }
                            />
                            <p className="text-[10px] text-text-muted leading-relaxed">
                              {s.activePlan.negotiationValidUntil
                                ? `No slide: ${formatNegotiationDeadlineSlideLabel(s.activePlan.negotiationValidUntil)}`
                                : 'Opcional. Aparece abaixo da fidelidade no bloco verde (ex.: Negociação válida para fechamentos até 15/06).'}
                            </p>
                          </div>

                          {/* Visual Bonus Destaque */}
                          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
                            <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
                              <h4 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-1.5">
                                <Award size={14} />
                                Bônus e Destaques PPT
                              </h4>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={s.activePlan.showBonus === true}
                                  onChange={(e) => s.handleUpdateActivePlanField('showBonus', e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-8 h-4 bg-white/[0.08] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-text-muted after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-accent peer-checked:after:bg-white" />
                                <span className="ml-2 text-[10px] font-semibold text-text-primary uppercase tracking-wider">Ativar Destaque</span>
                              </label>
                            </div>
                            {s.activePlan.showBonus === true && (
                              <Input
                                label="Texto em Destaque no Bloco Verde"
                                value={s.activePlan.bonus || ''}
                                onChange={(e) => s.handleUpdateActivePlanField('bonus', e.target.value)}
                                placeholder="Ex: Upgrade VIP incluso · Suporte Premium Grátis"
                              />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Scope checklists tab */}
                      {s.activeFormTab === 'scope' && (
                        <div className="space-y-4">
                          <div className="flex items-start justify-between gap-3 rounded-lg border border-accent/20 bg-accent/[0.06] px-3 py-2">
                            <p className="text-[10px] text-text-muted leading-relaxed">
                              Títulos das categorias podem ser editados aqui (salvos na proposta).
                              Para alterar itens do escopo, use{' '}
                              <strong className="text-text-primary">Configuração → Financeiro → Planos</strong>{' '}
                              e depois <strong className="text-text-primary">Atualizar escopo</strong>.
                            </p>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="shrink-0 text-[10px] h-7"
                              onClick={s.handleResyncActiveFromCatalog}
                            >
                              Atualizar escopo
                            </Button>
                          </div>
                          {s.activePlan.services?.map((cat: any, catIdx: number) => {
                            const included = (cat.services || []).filter((s: any) => s.isIncluded !== false)

                            return (
                              <div key={`${cat.category}-${catIdx}`} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
                                <div className="flex items-center gap-2 border-b border-white/[0.06] pb-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                                  <input
                                    type="text"
                                    value={cat.category || cat.categoria || ''}
                                    onChange={(e) => s.handleRenameCategory(catIdx, e.target.value)}
                                    className="flex-1 min-w-0 bg-transparent text-[11px] font-bold text-text-primary tracking-wide outline-none focus:ring-1 focus:ring-accent/40 rounded px-1"
                                    aria-label="Título da categoria no slide"
                                  />
                                  <span className="text-[9px] text-text-muted bg-white/[0.04] px-1 rounded shrink-0">
                                    {included.length} itens
                                  </span>
                                </div>
                                <ul className="space-y-1.5">
                                  {included.map((svc: any) => (
                                    <li
                                      key={svc.name}
                                      className="flex items-start gap-2 text-xs text-text-primary py-1.5 px-2 rounded-lg bg-accent/[0.04] border border-accent/15"
                                    >
                                      <span className="text-accent font-bold shrink-0">✓</span>
                                      <span className="min-w-0">
                                        {svc.name}
                                        {svc.quantity ? (
                                          <span className="text-text-muted"> ({svc.quantity})</span>
                                        ) : null}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )
                          })}
                        </div>
                      )}
    </div>
  )
}
